/**
 * LobbyScreen — 遊戲大廳/配對 UI
 *
 * 登入後的主畫面，提供：
 * - 顯示玩家名稱、MMR
 * - 「尋找對手」按鈕 → 加入配對佇列
 * - 配對中顯示等待動畫 + 「取消」按鈕
 * - 「單機練習」按鈕
 * - 「牌組構築」按鈕
 * - 「登出」按鈕
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';
import { useDeckStore } from '../../store/deckStore';
import {
  enqueueMatch,
  cancelMatch,
  startAIMatch,
  getWSClient,
  clearTokens,
} from '../../services/NetworkManager';
import {
  onMatchStateChange,
  stopListening,
} from '../../services/GameEventHandler';

type MatchState = 'idle' | 'searching' | 'found' | 'timeout';

export function LobbyScreen() {
  const { player, token, logout } = useAuthStore();
  const { navigateTo, setGameMode } = useUIStore();
  const { serverDecks, isLoadingDecks, selectedDeckId, selectDeckForBattle, fetchDecks } = useDeckStore();

  const [matchState, setMatchState] = useState<MatchState>('idle');
  const [searchTime, setSearchTime] = useState(0);
  const [matchError, setMatchError] = useState<string | null>(null);

  // Fetch decks on mount if empty
  useEffect(() => {
    if (serverDecks.length === 0 && !isLoadingDecks) {
      void fetchDecks();
    }
  }, [serverDecks.length, isLoadingDecks, fetchDecks]);

  // 配對狀態回調
  useEffect(() => {
    onMatchStateChange((state) => {
      switch (state) {
        case 'found':
          setMatchState('found');
          // game_start 事件會自動觸發導航到遊戲畫面
          break;
        case 'timeout':
          setMatchState('timeout');
          setMatchError('配對超時，請重試');
          break;
        case 'cancelled':
          setMatchState('idle');
          break;
        default:
          break;
      }
    });

    return () => {
      onMatchStateChange(null);
    };
  }, []);

  // 搜尋計時器
  useEffect(() => {
    if (matchState !== 'searching') return;

    const timer = setInterval(() => {
      setSearchTime((prev) => prev + 1);
    }, 1000);

    return () => { clearInterval(timer); };
  }, [matchState]);

  // 開始搜尋對手
  const handleFindMatch = useCallback(async () => {
    if (!token) return;
    setMatchState('searching');
    setSearchTime(0);
    setMatchError(null);

    try {
      await enqueueMatch(token, selectedDeckId ?? undefined);
    } catch (err) {
      const error = err as Error;
      setMatchError(error.message || '加入配對佇列失敗');
      setMatchState('idle');
    }
  }, [token, selectedDeckId]);

  // 取消搜尋
  const handleCancelSearch = useCallback(async () => {
    if (!token) return;
    try {
      await cancelMatch(token);
    } catch {
      // 忽略取消錯誤
    }
    setMatchState('idle');
    setSearchTime(0);
  }, [token]);

  // 單機練習
  const handleLocalGame = useCallback(() => {
    setGameMode('local');
    navigateTo('mainMenu');
  }, [navigateTo, setGameMode]);

  // AI 對戰
  const handleAIMatch = useCallback(async () => {
    if (!token || !selectedDeckId) return;
    setMatchState('searching');
    setSearchTime(0);
    setMatchError(null);

    try {
      await startAIMatch(token, selectedDeckId, 'balanced');
      // match_found event will be sent via WebSocket, triggering navigation
    } catch (err) {
      const error = err as Error;
      setMatchError(error.message || 'AI 對戰啟動失敗');
      setMatchState('idle');
    }
  }, [token, selectedDeckId]);

  // 牌組構築
  const handleDeckBuilder = useCallback(() => {
    navigateTo('deckBuilder');
  }, [navigateTo]);

  // 商城
  const handleShop = useCallback(() => {
    navigateTo('shop');
  }, [navigateTo]);

  // 登出
  const handleLogout = useCallback(() => {
    // 斷開 WebSocket
    const wsClient = getWSClient();
    wsClient.disconnect();
    stopListening();
    clearTokens();
    logout();
    navigateTo('auth');
  }, [logout, navigateTo]);

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div style={styles.container}>
      {/* 標題 */}
      <h1 style={styles.title}>星靈王</h1>

      {/* 玩家資訊 */}
      <div style={styles.playerInfo}>
        <span style={styles.playerName}>{player?.username || '未知'}</span>
        <div style={styles.statsRow}>
          <span style={styles.stat}>MMR: {String(player?.mmr ?? 1000)}</span>
          <span style={styles.stat}>
            勝 {String(player?.wins ?? 0)} / 負 {String(player?.losses ?? 0)}
          </span>
        </div>
      </div>

      {/* 主要操作區 */}
      <div style={styles.actionArea}>
        {matchState === 'idle' && (
          <>
            {/* 牌組選擇 */}
            {isLoadingDecks ? (
              <div style={styles.deckSelectLabel}>載入牌組中...</div>
            ) : serverDecks.length > 0 ? (
              <div style={styles.deckSelectGroup}>
                <div style={styles.deckSelectLabel}>選擇牌組</div>
                <select
                  style={styles.deckSelect}
                  value={selectedDeckId ?? ''}
                  onChange={(e) => { selectDeckForBattle(e.target.value); }}
                >
                  {serverDecks.map((deck) => (
                    <option key={deck.id} value={deck.id}>
                      {deck.name} ({deck.faction})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div style={styles.noDeckWarning}>
                尚無牌組，請先建立牌組
              </div>
            )}

            <button
              style={{
                ...styles.primaryButton,
                opacity: serverDecks.length > 0 ? 1 : 0.5,
              }}
              onClick={() => { void handleFindMatch(); }}
              disabled={serverDecks.length === 0}
            >
              尋找對手
            </button>
            <button
              style={{
                ...styles.aiButton,
                opacity: serverDecks.length > 0 ? 1 : 0.5,
              }}
              onClick={() => { void handleAIMatch(); }}
              disabled={serverDecks.length === 0}
            >
              🤖 AI 對戰
            </button>
            <button style={styles.secondaryButton} onClick={handleLocalGame}>
              單機練習
            </button>
            <button style={styles.secondaryButton} onClick={handleDeckBuilder}>
              牌組構築
            </button>
            <button style={{...styles.secondaryButton, backgroundColor: '#5a4a2a'}} onClick={handleShop}>
              🎴 商城
            </button>
            <button style={{...styles.secondaryButton, backgroundColor: '#2a4a5a'}} onClick={() => { navigateTo('quests'); }}>
              📋 每日任務
            </button>
          </>
        )}

        {matchState === 'searching' && (
          <div style={styles.searchingArea}>
            <div style={styles.spinner} />
            <p style={styles.searchingText}>正在尋找對手...</p>
            <p style={styles.searchTime}>{formatTime(searchTime)}</p>
            <button
              style={styles.cancelButton}
              onClick={() => { void handleCancelSearch(); }}
            >
              取消
            </button>
          </div>
        )}

        {matchState === 'found' && (
          <div style={styles.foundArea}>
            <p style={styles.foundText}>✨ 配對成功！</p>
            <p style={styles.foundSubtext}>準備進入對戰...</p>
          </div>
        )}

        {matchState === 'timeout' && (
          <div style={styles.timeoutArea}>
            <p style={styles.timeoutText}>配對超時</p>
            <button style={styles.primaryButton} onClick={() => { void handleFindMatch(); }}>
              重新搜尋
            </button>
          </div>
        )}
      </div>

      {/* 錯誤訊息 */}
      {matchError && <div style={styles.error}>{matchError}</div>}

      {/* 登出 */}
      <button style={styles.logoutButton} onClick={handleLogout}>
        登出
      </button>
    </div>
  );
}

// ============================================================
// 樣式
// ============================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100vw',
    height: '100vh',
    backgroundColor: '#1a1a2e',
    fontFamily: 'Arial, sans-serif',
    gap: '16px',
  },
  title: {
    color: '#e8d282',
    fontSize: '48px',
    margin: 0,
    textShadow: '0 0 20px rgba(232, 210, 130, 0.4)',
  },
  playerInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '16px',
  },
  playerName: {
    color: '#ffffff',
    fontSize: '24px',
    fontWeight: 'bold',
  },
  statsRow: {
    display: 'flex',
    gap: '16px',
  },
  stat: {
    color: '#888888',
    fontSize: '14px',
  },
  actionArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '280px',
    alignItems: 'stretch',
  },
  primaryButton: {
    padding: '16px',
    fontSize: '20px',
    backgroundColor: '#e8d282',
    color: '#1a1a2e',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontFamily: 'Arial, sans-serif',
  },
  aiButton: {
    padding: '14px',
    fontSize: '18px',
    backgroundColor: '#4a7a4a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontFamily: 'Arial, sans-serif',
  },
  secondaryButton: {
    padding: '14px',
    fontSize: '16px',
    backgroundColor: '#4a4a6a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
  },
  searchingArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #3a3a5a',
    borderTop: '3px solid #e8d282',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  searchingText: {
    color: '#e8d282',
    fontSize: '18px',
    margin: 0,
  },
  searchTime: {
    color: '#666666',
    fontSize: '24px',
    fontFamily: 'monospace',
    margin: 0,
  },
  cancelButton: {
    padding: '10px 32px',
    fontSize: '14px',
    backgroundColor: '#5a3a3a',
    color: '#ff8888',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
    marginTop: '8px',
  },
  foundArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  foundText: {
    color: '#e8d282',
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
  },
  foundSubtext: {
    color: '#888888',
    fontSize: '16px',
    margin: 0,
  },
  timeoutArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  timeoutText: {
    color: '#ff8888',
    fontSize: '18px',
    margin: 0,
  },
  error: {
    color: '#ff6666',
    fontSize: '14px',
    padding: '8px 16px',
    backgroundColor: 'rgba(255, 102, 102, 0.1)',
    borderRadius: '4px',
  },
  logoutButton: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    padding: '8px 16px',
    fontSize: '13px',
    backgroundColor: '#3a3a5a',
    color: '#888888',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
  },
  deckSelectGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '8px',
  },
  deckSelectLabel: {
    color: '#aaaaaa',
    fontSize: '14px',
    textAlign: 'center' as const,
  },
  deckSelect: {
    padding: '10px 14px',
    fontSize: '16px',
    backgroundColor: '#2a2a4a',
    color: '#ffffff',
    border: '1px solid #4a4a6a',
    borderRadius: '6px',
    outline: 'none',
    fontFamily: 'Arial, sans-serif',
    cursor: 'pointer',
  },
  noDeckWarning: {
    color: '#ff8888',
    fontSize: '14px',
    textAlign: 'center' as const,
    padding: '12px',
    backgroundColor: 'rgba(255, 136, 136, 0.1)',
    borderRadius: '6px',
    marginBottom: '8px',
  },
};
